import { forwardRef, type ElementRef, type ReactNode } from 'react';
import {
  Dialog as HUIDialog,
  DialogPanel as HUIDialogPanel,
  DialogTitle as HUIDialogTitle,
  Description as HUIDescription,
  DialogBackdrop as HUIDialogBackdrop,
  type DialogProps as HUIDialogProps,
  type DialogPanelProps as HUIDialogPanelProps,
  type DialogTitleProps as HUIDialogTitleProps,
  type DescriptionProps as HUIDescriptionProps,
} from '@headlessui/react';
import { X } from 'lucide-react';
import { cn } from '../../../lib/cn.js';

/**
 * Glass-surface Dialog built on Headless UI v2.
 * Includes modal backdrop, focus trap, and ESC-to-close out of the box.
 */
export type DialogProps = HUIDialogProps;

export function Dialog({ className, children, ...rest }: DialogProps) {
  const resolvedClass = typeof className === 'string' ? className : undefined;
  return (
    <HUIDialog {...rest} className={cn('relative z-50', resolvedClass)}>
      <HUIDialogBackdrop
        transition
        className={cn(
          'fixed inset-0 bg-black/60 backdrop-blur-sm',
          'transition duration-150 ease-out data-[closed]:opacity-0',
        )}
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {children as ReactNode}
      </div>
    </HUIDialog>
  );
}

export type DialogPanelProps = HUIDialogPanelProps;

export const DialogPanel = forwardRef<ElementRef<typeof HUIDialogPanel>, DialogPanelProps>(
  function DialogPanel({ className, children, ...rest }, ref) {
    const resolvedClass = typeof className === 'string' ? className : undefined;
    return (
      <HUIDialogPanel
        ref={ref}
        transition
        className={cn(
          'relative w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-surface-elevated/95 shadow-xl backdrop-blur-md',
          'transition duration-150 ease-out',
          'data-[closed]:scale-95 data-[closed]:opacity-0',
          resolvedClass,
        )}
        {...rest}
      >
        {children}
      </HUIDialogPanel>
    );
  },
);

export type DialogTitleProps = HUIDialogTitleProps;

export const DialogTitle = forwardRef<ElementRef<typeof HUIDialogTitle>, DialogTitleProps>(
  function DialogTitle({ className, children, ...rest }, ref) {
    const resolvedClass = typeof className === 'string' ? className : undefined;
    return (
      <HUIDialogTitle
        ref={ref}
        className={cn('text-h3 font-semibold text-primary', resolvedClass)}
        {...rest}
      >
        {children}
      </HUIDialogTitle>
    );
  },
);

export type DialogDescriptionProps = HUIDescriptionProps;

export function DialogDescription({ className, children, ...rest }: DialogDescriptionProps) {
  const resolvedClass = typeof className === 'string' ? className : undefined;
  return (
    <HUIDescription
      className={cn('mt-1 text-small text-secondary leading-relaxed', resolvedClass)}
      {...rest}
    >
      {children}
    </HUIDescription>
  );
}

interface SectionProps {
  className?: string;
  children?: ReactNode;
}

/** Scrollable content area of the dialog. */
export function DialogBody({ className, children }: SectionProps) {
  return <div className={cn('max-h-[60vh] overflow-y-auto px-5 py-4', className)}>{children}</div>;
}

/** Footer row for actions (right-aligned on wide screens). */
export function DialogFooter({ className, children }: SectionProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 border-t border-white/[0.04] bg-canvas/40 px-5 py-3 sm:flex-row sm:justify-end',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface DialogCloseProps {
  onClick: () => void;
  label: string;
  className?: string;
}

/** Close button rendered in the top-right corner. */
export function DialogClose({ onClick, label, className }: DialogCloseProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'absolute right-3 top-3 rounded-md p-1.5 text-secondary transition-colors hover:bg-white/[0.06] hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
        className,
      )}
    >
      <X className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}
